/**
 * Campaign Service
 * Handles all campaign-related API calls
 */

import apiService from './api.service';
import { API_ENDPOINTS } from '../config/api.config';
import {
  Campaign,
  CampaignListParams,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  CampaignStatsResponse,
  CampaignsListResponse,
} from '../types/api.types';
import { normalizeCreateCampaignRequest } from '../utils/campaignValidation';

class CampaignService {
  /**
   * Get all campaigns with filtering
   */
  async getCampaigns(params?: CampaignListParams): Promise<CampaignsListResponse> {
    return await apiService.get<CampaignsListResponse>(
      API_ENDPOINTS.CAMPAIGN.LIST,
      { params }
    );
  }

  /**
   * Search campaigns by query with pagination
   */
  async searchCampaigns(params: { q: string; page?: number; limit?: number }): Promise<CampaignsListResponse> {
    return await apiService.get<CampaignsListResponse>(
      API_ENDPOINTS.CAMPAIGN.SEARCH,
      { params }
    );
  }

  /**
   * Get campaign by ID
   */
  async getCampaignById(id: string): Promise<Campaign> {
    return await apiService.get<Campaign>(API_ENDPOINTS.CAMPAIGN.GET_BY_ID(id));
  }

  /**
   * Create new campaign
   */
  async createCampaign(data: CreateCampaignRequest): Promise<Campaign> {
    return await apiService.post<Campaign>(
      API_ENDPOINTS.CAMPAIGN.CREATE,
      normalizeCreateCampaignRequest(data)
    );
  }

  /**
   * Update campaign
   */
  async updateCampaign(id: string, data: UpdateCampaignRequest): Promise<Campaign> {
    return await apiService.put<Campaign>(
      API_ENDPOINTS.CAMPAIGN.UPDATE(id),
      data
    );
  }

  /**
   * Delete campaign
   */
  async deleteCampaign(id: string): Promise<void> {
    await apiService.delete(API_ENDPOINTS.CAMPAIGN.DELETE(id));
  }

  /**
   * Approve campaign
   */
  async approveCampaign(id: string, data?: { level?: number; comments?: string }): Promise<Campaign> {
    return await apiService.post<Campaign>(API_ENDPOINTS.CAMPAIGN.APPROVE(id), data);
  }

  /**
   * Reject campaign
   */
  async rejectCampaign(id: string, data?: { level?: number; comments?: string }): Promise<Campaign> {
    return await apiService.post<Campaign>(
      API_ENDPOINTS.CAMPAIGN.REJECT(id),
      data
    );
  }

  /**
   * Get campaign statistics
   */
  async getCampaignStats(): Promise<CampaignStatsResponse> {
    return await apiService.get<CampaignStatsResponse>(API_ENDPOINTS.CAMPAIGN.STATS);
  }

  /**
   * Create case on WordPress
   */
  async createWordPressCase(data: {
    company_name: string;
    what_is_this_about: string;
    what_happened: string;
    what_information_was_impacted: string;
    what_information_was_impacted_array: string[];
    about_company: string;
    since_date: string;
    campaign_name: string;
    lead_type: string;
    campaign_unique_id: string;
    enable_single_level_form?: boolean;
  }): Promise<{ success: boolean; post_id?: number; message?: string; preview_url?: string }> {
    const wpApiUrl = process.env.REACT_APP_WP_API_URL;
    const wpAuth = process.env.REACT_APP_WP_AUTH_TOKEN;
    
    if (!wpApiUrl || !wpAuth) {
      throw new Error('WordPress API configuration is missing. Please check your environment variables.');
    }
    
    const response = await fetch(wpApiUrl, {
      method: 'POST',
      headers: {
        'Authorization': wpAuth,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`WordPress API error: ${response.statusText}`);
    }

    return await response.json();
  }
}

const campaignService = new CampaignService();
export default campaignService;
